import TextField from 'components/Form/TextField';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import Button from 'components/Form/Button';
import { SpecialInstruction } from 'apps/user/types/user.enum.type';
import { useSpecialInstructionUpdate } from 'common/queries-and-mutations/user';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchUserInformation } from 'thunks/account-thunk';
import { selectAccountDetails } from 'selectors/account-selector';
import htmlToDraft from 'html-to-draftjs';
import { useNavigate } from 'react-router-dom';
import withCreatePortal from 'components/HOC/withCreatePortal';
import SuccessModal from 'components/Modal/SuccessModal';

const staticFormValues: SpecialInstruction = {
  instructionTitle: '',
  instructionDescription: ''
};

const EnhancedSuccessModal = withCreatePortal(SuccessModal);

export default function SpecialInstructions() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const navigate = useNavigate();

  const { mutate, isSuccess, isError, isLoading } =
    useSpecialInstructionUpdate();

  const [descEditorState, setDescEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [fieldValue, setFieldValue] = useState(staticFormValues);

  const formik = useFormik({
    initialValues: { ...fieldValue },
    enableReinitialize: true,
    validationSchema: Yup.object({
      instructionTitle: Yup.string().required('Title is required').nullable(),
      instructionDescription: Yup.string()
        .required('Description is required')
        .nullable()
    }),
    onSubmit: (values, { setFieldTouched }) => {
      for (const key in values) {
        setFieldTouched(key, true);
      }
      handleSubmit(values);
    }
  });

  const handleTermsInputState = useCallback((data: SpecialInstruction) => {
    const termsContentBlock = htmlToDraft(data.instructionDescription);
    const termsContentState = ContentState.createFromBlockArray(
      termsContentBlock.contentBlocks
    );
    const termsEditorState = EditorState.createWithContent(termsContentState);
    setDescEditorState(termsEditorState);
  }, []);

  const setEditorState = useCallback(
    (data: SpecialInstruction) => {
      handleTermsInputState(data);
    },
    [handleTermsInputState]
  );

  const getStaticCms = useCallback(async () => {
    setEditorState({
      instructionTitle: user.instructionTitle,
      instructionDescription: user.instructionDescription
    });
    setFieldValue({
      instructionTitle: user.instructionTitle,
      instructionDescription: user.instructionDescription
    });
  }, [user.instructionDescription, user.instructionTitle, setEditorState]);

  useEffect(() => {
    getStaticCms();
  }, [getStaticCms]);

  const handleSubmit = (formValues: SpecialInstruction) => {
    mutate(formValues);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      dispatch(fetchUserInformation());
      setShowSuccessModal(true);
      setIsSubmitting(false);
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <div>
      <Breadcrumb title="Special Instructions" />
      <form autoComplete="false" onSubmit={formik.handleSubmit}>
        <div className="mt-10 space-y-10 rounded-md bg-white p-10">
          <TextField
            name="instructionTitle"
            placeholder="Instruction Title"
            value={formik.values.instructionTitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label=""
            error={
              formik.touched.instructionTitle
                ? formik.errors.instructionTitle
                : ''
            }
            size="lg"
            className="rounded-lg border-[1.5px] border-gray-300"
          />
          <Editor
            editorState={descEditorState}
            toolbarClassName="toolbarClassName"
            wrapperClassName="rounded-[24rem] h-[30rem]"
            editorClassName="rounded-[4px] border p-[10px] border-[1px] border-[#EBEBEB] text-[#626465] text-[16px]"
            onEditorStateChange={newState => {
              if (newState) {
                setDescEditorState(newState);
                formik.setFieldValue(
                  'instructionDescription',
                  draftToHtml(convertToRaw(newState.getCurrentContent()))
                );
              }
            }}
            // toolbar={{
            //   options: [
            //     'inline',
            //     'blockType',
            //     'fontSize',
            //     'list',
            //     'textAlign',
            //     'link',
            //     'embedded',
            //     'image',
            //     'history'
            //   ]
            // }}
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="md"
              className="mt-10 w-[13rem] rounded p-[0.2rem]"
              label="Create Instruction"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </div>
      </form>

      {showSuccessModal && (
        <EnhancedSuccessModal
          title={'Instruction created Successfully'}
          onProccessClick={() => {
            navigate('/dashboard/will-summary');
          }}
          buttonText="Close"
        />
      )}
    </div>
  );
}
